import { createClient } from '@supabase/supabase-js'
import { RealtimeChannel } from '@supabase/supabase-js'
import { normalizeEmail, normalizePersonName } from '../utils/normalizeUserFields'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

if (import.meta.env.PROD && (!supabaseUrl || !supabaseAnonKey)) {
  throw new Error(
    'Variáveis VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY são obrigatórias em produção.'
  )
}

const resolvedSupabaseUrl = supabaseUrl || 'https://your-project.supabase.co'
const resolvedSupabaseAnonKey = supabaseAnonKey || 'your-anon-key'

export const supabase = createClient(resolvedSupabaseUrl, resolvedSupabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

// Tipos para TypeScript
export interface User {
  id: string
  email: string
  name?: string
  created_at: string
  updated_at: string
}

export interface AuthError {
  message: string
  status?: number
}

// Funções de autenticação
export const auth = {
  // Login com email e senha
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  // Registro com email e senha
  signUp: async (email: string, password: string, name?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email: normalizeEmail(email),
      password,
      options: {
        data: {
          name: name ? normalizePersonName(name) : name
        }
      }
    })
    return { data, error }
  },

  // Logout
  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  // Recuperação de senha
  resetPassword: async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(normalizeEmail(email), {
      redirectTo: `${window.location.origin}/reset-password`
    })
    return { data, error }
  },

  // Atualizar senha
  updatePassword: async (password: string) => {
    const { data, error } = await supabase.auth.updateUser({
      password: password
    })
    return { data, error }
  },

  // Obter usuário atual
  getCurrentUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  },

  // Escutar mudanças de autenticação
  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Funções de banco de dados
export const database = {
  // Operações CRUD genéricas
  select: async (table: string, columns: string = '*', filters?: any) => {
    let query = supabase.from(table).select(columns)
    
    if (filters) {
      Object.keys(filters).forEach(key => {
        query = query.eq(key, filters[key])
      })
    }
    
    const { data, error } = await query
    return { data, error }
  },

  insert: async (table: string, data: any) => {
    const { data: result, error } = await supabase
      .from(table)
      .insert(data)
      .select()
    return { data: result, error }
  },

  update: async (table: string, data: any, filters: any) => {
    let query = supabase.from(table).update(data)
    
    Object.keys(filters).forEach(key => {
      query = query.eq(key, filters[key])
    })
    
    const { data: result, error } = await query.select()
    return { data: result, error }
  },

  delete: async (table: string, filters: any) => {
    let query = supabase.from(table).delete()
    
    Object.keys(filters).forEach(key => {
      query = query.eq(key, filters[key])
    })
    
    const { data, error } = await query
    return { data, error }
  }
}

// Funções de storage
export const storage = {
  // Upload de arquivo
  upload: async (bucket: string, path: string, file: File) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file)
    return { data, error }
  },

  // Download de arquivo
  download: async (bucket: string, path: string) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(path)
    return { data, error }
  },

  // Obter URL pública
  getPublicUrl: (bucket: string, path: string) => {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path)
    return data.publicUrl
  },

  // Listar arquivos
  list: async (bucket: string, path?: string) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(path)
    return { data, error }
  },

  // Deletar arquivo
  remove: async (bucket: string, paths: string[]) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .remove(paths)
    return { data, error }
  }
}

// Funções de realtime
export const realtime = {
  // Inscrever em mudanças de tabela
  subscribe: (table: string, callback: (payload: any) => void): RealtimeChannel => {
    return supabase
      .channel(`public:${table}`)
      .on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table: table },
        callback
      )
      .subscribe()
  },

  // Inscrever em mudanças específicas
  subscribeToChanges: (table: string, event: 'INSERT' | 'UPDATE' | 'DELETE', callback: (payload: any) => void): RealtimeChannel => {
    return supabase
      .channel(`public:${table}`)
      .on(
        'postgres_changes' as any,
        { event: event, schema: 'public', table: table },
        callback
      )
      .subscribe()
  }
}

// Funções de edge functions
export const edgeFunctions = {
  // Chamar edge function
  invoke: async (functionName: string, body?: any) => {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: body
    })
    return { data, error }
  }
}

export default supabase
